/**
 * Pure filtering and counting. Kept free of React and of the stores so it can
 * be unit-tested directly and reused by any future view.
 */

import type { TrackerGroup, TrackerItem, TrackerSet } from './content.types';
import type { FilterState } from '@/state/settingsStore';

/**
 * Search-normalizes a string: case-folded, ё/е-merged, accent-stripped.
 * Deliberately language-agnostic — a Russian-mode user pasting an English name
 * from a trade Discord must still find the row.
 */
export function foldForSearch(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-̈̊-ͯ]/g, '') // strip accents, keep й/ё composition sane
    .normalize('NFC')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[«»""'']/g, '"')
    .trim();
}

/** Every searchable string on an item, in both languages. */
export function searchHaystack(item: TrackerItem): string {
  return foldForSearch(
    [
      item.name.en,
      item.name.ru,
      item.source?.en,
      item.source?.ru,
      item.notes?.en,
      item.notes?.ru,
      item.dropChance,
      ...(item.tags ?? []),
    ]
      .filter(Boolean)
      .join(' '),
  );
}

export interface FilterContext {
  isDone: (itemId: string) => boolean;
}

export function itemMatches(item: TrackerItem, filters: FilterState, ctx: FilterContext): boolean {
  if (filters.status !== 'all') {
    const done = ctx.isDone(item.id);
    if (filters.status === 'done' && !done) return false;
    if (filters.status === 'todo' && done) return false;
  }

  if (filters.rarities.length > 0 && !filters.rarities.includes(item.rarity ?? 'unknown')) {
    return false;
  }

  const query = foldForSearch(filters.search);
  if (query.length > 0) {
    const haystack = searchHaystack(item);
    // All whitespace-separated terms must appear, in any order.
    for (const term of query.split(/\s+/).filter(Boolean)) {
      if (!haystack.includes(term)) return false;
    }
  }

  return true;
}

/**
 * Does the search match the group's own title, in either language?
 *
 * Items carry no place name: in the mask set every item is a corpse
 * description, so "teapot" can only find The Giant Teapot through its group.
 * All whitespace-separated terms must appear, the same rule as item search.
 */
export function groupTitleMatches(group: TrackerGroup, search: string): boolean {
  const query = foldForSearch(search);
  if (query.length === 0) return false;
  const hay = foldForSearch(`${group.title.en} ${group.title.ru}`);
  return query
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

/**
 * The filters to apply to a group's items. A title match already satisfies
 * the search, so only status and rarity remain to be checked per item.
 */
export function filtersWithinGroup(group: TrackerGroup, filters: FilterState): FilterState {
  return groupTitleMatches(group, filters.search) ? { ...filters, search: '' } : filters;
}

export interface VisibleGroup {
  group: TrackerGroup;
  items: TrackerItem[];
  /** Done / total across the WHOLE group, not just the visible items — a
   *  filtered view should still report real progress. */
  done: number;
  total: number;
}

export function selectVisibleGroups(
  set: TrackerSet,
  filters: FilterState,
  ctx: FilterContext,
): VisibleGroup[] {
  const out: VisibleGroup[] = [];
  for (const group of set.groups) {
    const within = filtersWithinGroup(group, filters);
    const items = group.items.filter((item) => itemMatches(item, within, ctx));
    if (items.length === 0) continue;
    out.push({
      group,
      items,
      done: group.items.filter((i) => ctx.isDone(i.id)).length,
      total: group.items.length,
    });
  }
  return out;
}

export interface SetStats {
  done: number;
  total: number;
  percent: number;
  perGroup: { id: string; done: number; total: number }[];
}

export function selectStats(set: TrackerSet, ctx: FilterContext): SetStats {
  const perGroup = set.groups.map((group) => ({
    id: group.id,
    done: group.items.filter((i) => ctx.isDone(i.id)).length,
    total: group.items.length,
  }));
  const done = perGroup.reduce((n, g) => n + g.done, 0);
  const total = perGroup.reduce((n, g) => n + g.total, 0);
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    perGroup,
  };
}

export function allItemIds(set: TrackerSet): string[] {
  return set.groups.flatMap((g) => g.items.map((i) => i.id));
}
